// @ts-nocheck
import React, {useEffect, useRef, useState} from 'react'
import AffiliateShell from './components/AffiliateShell'
import {AFF, card} from './components/affiliateTheme'
import {getWhatsappStatus, connectWhatsapp, logoutWhatsapp, getWaTemplates, saveWaTemplates, saveAdminWaTemplates, uploadWaTemplateMedia} from './core/_requests'

const isAdminMode = () => {
  try {
    return !!localStorage.getItem('admin_auth_token')
  } catch (_) {
    return false
  }
}

// Thumbnail grid for a template's media. `onRemove` omitted ⇒ read-only.
const MediaThumbs = ({media, onRemove}: any) => {
  if (!media || !media.length) return null
  return (
    <div style={{display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10}}>
      {media.map((m, mi) => (
        <div
          key={mi}
          style={{position: 'relative', width: 64, height: 64, borderRadius: 8, overflow: 'hidden', border: `1px solid ${AFF.border}`, background: '#f5f5f5'}}
        >
          {m.type === 'video' ? (
            <video src={m.url} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
          ) : m.type === 'image' ? (
            <img src={m.url} alt='' style={{width: '100%', height: '100%', objectFit: 'cover'}} />
          ) : (
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: 24, color: AFF.muted}}>
              <i className='bi bi-file-earmark' />
            </div>
          )}
          {onRemove && (
            <button
              onClick={() => onRemove(mi)}
              title='הסר'
              style={{position: 'absolute', top: 2, insetInlineEnd: 2, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,.6)', color: '#fff', fontSize: 11, lineHeight: '18px', padding: 0, cursor: 'pointer'}}
            >
              ×
            </button>
          )}
        </div>
      ))}
    </div>
  )
}

// Textarea that grows to fit its content, so long messages never scroll inside it.
const AutoTextarea = ({value, onChange, placeholder}: any) => {
  const ref = useRef<any>(null)
  const resize = () => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = Math.max(140, el.scrollHeight) + 'px'
  }
  useEffect(() => {
    resize()
  }, [value])
  return (
    <textarea
      ref={ref}
      className='form-control'
      style={{border: `1px solid ${AFF.border}`, minHeight: 140, overflow: 'hidden', resize: 'none'}}
      placeholder={placeholder}
      value={value}
      onChange={(e) => {
        onChange(e.target.value)
        resize()
      }}
    />
  )
}

// One editable template (title + text + media). Shared by the affiliate's own
// templates and the admin "for all affiliates" editor.
const EditableTemplateCard = ({t, onTitle, onText, onRemove, onPickMedia, onRemoveMedia, uploading, onPromote, promoting}: any) => (
  <div style={{border: `1px solid ${AFF.border}`, borderRadius: 12, padding: 14, marginBottom: 12}}>
    <div style={{display: 'flex', gap: 8, marginBottom: 8}}>
      <input
        className='form-control form-control-sm'
        style={{border: `1px solid ${AFF.border}`, fontWeight: 600}}
        placeholder='כותרת (למשל: פתיחה)'
        value={t.title}
        onChange={(e) => onTitle(e.target.value)}
      />
      {onPromote && (
        <button
          className='btn btn-sm btn-light-primary'
          onClick={onPromote}
          disabled={promoting}
          title='העבר הודעה זו לכל השותפים'
          style={{whiteSpace: 'nowrap'}}
        >
          {promoting ? <span className='spinner-border spinner-border-sm' /> : <><i className='bi bi-megaphone me-1' /> לכל השותפים</>}
        </button>
      )}
      <button className='btn btn-sm btn-light-danger' onClick={onRemove} title='מחק'>
        <i className='bi bi-trash' />
      </button>
    </div>
    <AutoTextarea value={t.text} onChange={onText} placeholder='תוכן ההודעה…' />
    <MediaThumbs media={t.media} onRemove={onRemoveMedia} />
    <label className='btn btn-sm btn-light-primary' style={{cursor: 'pointer', marginTop: 10, marginBottom: 0}}>
      {uploading ? (
        <>
          <span className='spinner-border spinner-border-sm me-1' /> מעלה…
        </>
      ) : (
        <>
          <i className='bi bi-image me-1' /> הוסף מדיה
        </>
      )}
      <input type='file' accept='image/*,video/*' multiple hidden disabled={uploading} onChange={onPickMedia} />
    </label>
  </div>
)

const AffiliateWhatsappWrapper = () => {
  const [state, setState] = useState<any>({status: 'disconnected'})
  const [busy, setBusy] = useState(false)
  const pollRef = useRef<any>(null)
  const [templates, setTemplates] = useState<any[]>([])
  const [savingTpl, setSavingTpl] = useState(false)
  const [tplSaved, setTplSaved] = useState(false)
  const [uploadingIdx, setUploadingIdx] = useState(-1)
  const [promotingIdx, setPromotingIdx] = useState(-1)

  // Admin-managed templates pushed to all affiliates. The current viewer can edit
  // them only in admin mode; otherwise they're shown read-only.
  const admin = isAdminMode()
  const [adminTemplates, setAdminTemplates] = useState<any[]>([])
  const [savingAdmin, setSavingAdmin] = useState(false)
  const [adminSaved, setAdminSaved] = useState(false)
  const [uploadingAdminIdx, setUploadingAdminIdx] = useState(-1)

  const refresh = async () => {
    try {
      const res = await getWhatsappStatus()
      setState(res.data || {status: 'disconnected'})
    } catch (_) {}
  }
  useEffect(() => {
    refresh()
    pollRef.current = setInterval(refresh, 3000)
    getWaTemplates()
      .then((r) => {
        setTemplates(r.data?.templates || [])
        setAdminTemplates(r.data?.admin_templates || [])
      })
      .catch(() => {})
    return () => clearInterval(pollRef.current)
  }, [])

  // Generic media upload: append the uploaded {url,type} items to the template at
  // `i` in whichever list the setter targets. Returns when done.
  const uploadInto = async (setList, setBusyIdx, i, e) => {
    const files = Array.from(e.target.files || []) as File[]
    e.target.value = ''
    if (!files.length) return
    setBusyIdx(i)
    // Upload in small batches: keeps each request modest (videos are big, and proxy
    // body-size limits would reject one huge 50-file request), shows progress, and a
    // failed batch doesn't lose the rest.
    const BATCH = 3
    let failed = 0
    for (let c = 0; c < files.length; c += BATCH) {
      const chunk = files.slice(c, c + BATCH)
      try {
        const res = await uploadWaTemplateMedia(chunk)
        const uploaded = res.data?.media || []
        setList((t) => t.map((x, idx) => (idx === i ? {...x, media: [...(x.media || []), ...uploaded]} : x)))
      } catch (err: any) {
        failed += chunk.length
        console.error('media batch upload failed:', err?.response?.data?.error || err?.message)
      }
    }
    if (failed) alert(`חלק מהקבצים לא הועלו (${failed} מתוך ${files.length}). נסו שוב את אלו שחסרים.`)
    setBusyIdx(-1)
  }
  const removeMediaIn = (setList, i, mi) =>
    setList((t) => t.map((x, idx) => (idx === i ? {...x, media: (x.media || []).filter((_, k) => k !== mi)} : x)))

  // Affiliate's own templates
  const setTpl = (i, key, val) => setTemplates((t) => t.map((x, idx) => (idx === i ? {...x, [key]: val} : x)))
  const addTpl = () => setTemplates((t) => [...t, {title: '', text: '', media: []}])
  const removeTpl = (i) => setTemplates((t) => t.filter((_, idx) => idx !== i))
  const onPickMedia = (i, e) => uploadInto(setTemplates, setUploadingIdx, i, e)
  const removeMedia = (i, mi) => removeMediaIn(setTemplates, i, mi)
  const saveTemplates = async () => {
    setSavingTpl(true)
    try {
      const res = await saveWaTemplates(templates.filter((t) => (t.text || '').trim() || (t.media || []).length))
      setTemplates(res.data?.templates || [])
      setTplSaved(true)
      setTimeout(() => setTplSaved(false), 1800)
    } catch (e: any) {
      alert('שמירה נכשלה: ' + (e?.response?.data?.error || e?.message))
    }
    setSavingTpl(false)
  }

  // Admin only: move one of the affiliate's own (private) templates to the
  // admin "all affiliates" list. Persists both lists immediately.
  const promoteToAdmin = async (i) => {
    const t = templates[i]
    if (!t) return
    const moved = {title: t.title || '', text: t.text || '', media: t.media || []}
    const newAdmin = [...adminTemplates, moved]
    const newPrivate = templates.filter((_, idx) => idx !== i)
    setPromotingIdx(i)
    try {
      const a = await saveAdminWaTemplates(newAdmin.filter((x) => (x.text || '').trim() || (x.media || []).length))
      const p = await saveWaTemplates(newPrivate.filter((x) => (x.text || '').trim() || (x.media || []).length))
      setAdminTemplates(a.data?.admin_templates || newAdmin)
      setTemplates(p.data?.templates || newPrivate)
    } catch (e: any) {
      alert('העברה נכשלה: ' + (e?.response?.data?.error || e?.message))
    }
    setPromotingIdx(-1)
  }

  // Admin templates (admin mode only)
  const setAdminTpl = (i, key, val) => setAdminTemplates((t) => t.map((x, idx) => (idx === i ? {...x, [key]: val} : x)))
  const addAdminTpl = () => setAdminTemplates((t) => [...t, {title: '', text: '', media: []}])
  const removeAdminTpl = (i) => setAdminTemplates((t) => t.filter((_, idx) => idx !== i))
  const onPickAdminMedia = (i, e) => uploadInto(setAdminTemplates, setUploadingAdminIdx, i, e)
  const removeAdminMedia = (i, mi) => removeMediaIn(setAdminTemplates, i, mi)
  const saveAdminTemplates = async () => {
    setSavingAdmin(true)
    try {
      const res = await saveAdminWaTemplates(adminTemplates.filter((t) => (t.text || '').trim() || (t.media || []).length))
      setAdminTemplates(res.data?.admin_templates || [])
      setAdminSaved(true)
      setTimeout(() => setAdminSaved(false), 1800)
    } catch (e: any) {
      alert('שמירה נכשלה: ' + (e?.response?.data?.error || e?.message))
    }
    setSavingAdmin(false)
  }

  const handleConnect = async () => {
    setBusy(true)
    try {
      await connectWhatsapp()
      await refresh()
    } catch (e: any) {
      alert('שירות הוואטסאפ אינו זמין כרגע: ' + (e?.response?.data?.error || e?.message))
    }
    setBusy(false)
  }
  const handleLogout = async () => {
    setBusy(true)
    try {
      await logoutWhatsapp()
      await refresh()
    } catch (_) {}
    setBusy(false)
  }

  const connected = state.status === 'connected'

  return (
    <AffiliateShell title='חיבור וואטסאפ' subtitle='חבר את המספר שלך כדי לשלוח הודעות ללקוחות ולוודא שהם עובדים.'>
      <div className='row g-4'>
        <div className='col-lg-7'>
          <div style={{...card, padding: 40, textAlign: 'center', minHeight: 360, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
            {connected ? (
              <>
                <div style={{width: 72, height: 72, borderRadius: '50%', background: '#e8fff3', color: AFF.success, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34, marginBottom: 16}}>
                  <i className='bi bi-whatsapp' />
                </div>
                <div style={{fontWeight: 700, fontSize: 18, color: AFF.ink}}>מחובר בהצלחה</div>
                <div dir='ltr' style={{fontSize: 22, fontWeight: 700, color: AFF.success, margin: '8px 0 4px'}}>{state.number}</div>
                <div style={{color: AFF.muted, fontSize: 13, marginBottom: 24}}>אתה יכול לשלוח הודעות מעמוד הלקוחות.</div>
                <button className='btn btn-light-danger' disabled={busy} onClick={handleLogout}>
                  <i className='bi bi-box-arrow-right me-2' /> ניתוק
                </button>
              </>
            ) : state.status === 'connecting' && state.qr ? (
              <>
                <div style={{fontWeight: 700, color: AFF.ink, marginBottom: 16}}>סרוק את הקוד</div>
                <div style={{padding: 12, background: '#fff', borderRadius: 16, border: `1px solid ${AFF.border}`}}>
                  <img src={state.qr} alt='WhatsApp QR' style={{width: 240, height: 240}} />
                </div>
                <div style={{color: AFF.muted, fontSize: 13, marginTop: 16}}>
                  <span className='spinner-border spinner-border-sm me-2' /> ממתין לסריקה…
                </div>
              </>
            ) : state.status === 'connecting' ? (
              <>
                <span className='spinner-border text-primary mb-3' style={{width: 48, height: 48}} />
                <div style={{color: AFF.muted}}>יוצר קוד QR…</div>
              </>
            ) : (
              <>
                <div style={{width: 72, height: 72, borderRadius: '50%', background: AFF.accentSoft, color: AFF.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34, marginBottom: 16}}>
                  <i className='bi bi-whatsapp' />
                </div>
                <div style={{fontWeight: 700, fontSize: 18, color: AFF.ink, marginBottom: 6}}>וואטסאפ לא מחובר</div>
                <div style={{color: AFF.muted, fontSize: 14, marginBottom: 24, maxWidth: 320}}>
                  לחץ כדי ליצור קוד QR וחבר את חשבון הוואטסאפ שלך.
                </div>
                <button className='btn' disabled={busy} onClick={handleConnect} style={{background: AFF.success, color: '#fff', fontWeight: 700, padding: '10px 28px', borderRadius: 10}}>
                  {busy ? 'מתחבר…' : 'חבר וואטסאפ'}
                </button>
              </>
            )}
          </div>
        </div>

        <div className='col-lg-5'>
          <div style={{...card, padding: 24}}>
            <div style={{fontWeight: 700, color: AFF.ink, marginBottom: 14}}>איך מחברים?</div>
            {[
              'לחץ על "חבר וואטסאפ" כדי ליצור קוד QR',
              'פתח וואטסאפ בטלפון ← הגדרות ← מכשירים מקושרים',
              'לחץ "קישור מכשיר" וסרוק את הקוד',
              'לאחר החיבור, שלח הודעות מעמוד הלקוחות',
            ].map((txt, i) => (
              <div key={i} style={{display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14}}>
                <div style={{width: 26, height: 26, borderRadius: '50%', background: AFF.accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0}}>
                  {i + 1}
                </div>
                <div style={{color: AFF.ink, fontSize: 14, paddingTop: 2}}>{txt}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Admin-managed messages for ALL affiliates. Editable in admin mode, otherwise
          shown read-only with a lock so affiliates can use but not change them. */}
      {admin ? (
        <div style={{...card, padding: 24, marginTop: 20, border: `1px solid ${AFF.accent}`}}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6}}>
            <div style={{fontWeight: 700, color: AFF.ink}}>
              <i className='bi bi-shield-lock me-2' style={{color: AFF.accent}} />
              הודעות מנהל — לכל השותפים
            </div>
            <button className='btn btn-sm' style={{background: AFF.accent, color: '#fff', fontWeight: 600}} disabled={savingAdmin} onClick={saveAdminTemplates}>
              {savingAdmin ? 'שומר…' : adminSaved ? 'נשמר ✓' : 'שמירה'}
            </button>
          </div>
          <div style={{fontSize: 13, color: AFF.muted, marginBottom: 16}}>
            הודעות אלו מופיעות אצל <b>כל השותפים</b> כהודעות מוכנות לשליחה — רק מנהל יכול לערוך אותן. השתמש ב-<code>{'{{name}}'}</code> כדי לשלב את שם הליד.
          </div>

          {adminTemplates.map((t, i) => (
            <EditableTemplateCard
              key={i}
              t={t}
              onTitle={(v) => setAdminTpl(i, 'title', v)}
              onText={(v) => setAdminTpl(i, 'text', v)}
              onRemove={() => removeAdminTpl(i)}
              onPickMedia={(e) => onPickAdminMedia(i, e)}
              onRemoveMedia={(mi) => removeAdminMedia(i, mi)}
              uploading={uploadingAdminIdx === i}
            />
          ))}

          <button className='btn btn-sm btn-light-primary' onClick={addAdminTpl}>
            <i className='bi bi-plus-lg me-1' /> הוסף הודעת מנהל
          </button>
        </div>
      ) : (
        adminTemplates.length > 0 && (
          <div style={{...card, padding: 24, marginTop: 20}}>
            <div style={{fontWeight: 700, color: AFF.ink, marginBottom: 6}}>
              <i className='bi bi-shield-lock me-2' style={{color: AFF.accent}} />
              הודעות מהמערכת
            </div>
            <div style={{fontSize: 13, color: AFF.muted, marginBottom: 16}}>
              הודעות מוכנות שהוגדרו על ידי הנהלת המערכת — ניתן לשלוח אותן ללידים מעמוד הסוכנים והלידים. לא ניתן לעריכה.
            </div>
            {adminTemplates.map((t, i) => (
              <div key={i} style={{border: `1px solid ${AFF.border}`, borderRadius: 12, padding: 14, marginBottom: 12, background: '#fafafa'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: AFF.ink, marginBottom: 6}}>
                  <i className='bi bi-lock-fill' style={{fontSize: 12, color: AFF.muted}} />
                  {t.title || 'הודעה'}
                </div>
                <div style={{fontSize: 13, color: AFF.ink, whiteSpace: 'pre-wrap'}}>{t.text}</div>
                <MediaThumbs media={t.media} />
              </div>
            ))}
          </div>
        )
      )}

      {/* Editable shortcut templates for cold-calling / recruiting leads */}
      <div style={{...card, padding: 24, marginTop: 20}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6}}>
          <div style={{fontWeight: 700, color: AFF.ink}}>הודעות מהירות (שורטקאטים)</div>
          <button className='btn btn-sm' style={{background: AFF.accent, color: '#fff', fontWeight: 600}} disabled={savingTpl} onClick={saveTemplates}>
            {savingTpl ? 'שומר…' : tplSaved ? 'נשמר ✓' : 'שמירה'}
          </button>
        </div>
        <div style={{fontSize: 13, color: AFF.muted, marginBottom: 16}}>
          הודעות מוכנות לשליחה מהירה ללידים בזמן שיחות קרות. השתמש ב-<code>{'{{name}}'}</code> כדי לשלב את שם הליד אוטומטית. אפשר לצרף תמונות וסרטונים שיישלחו יחד עם ההודעה.
        </div>

        {templates.map((t, i) => (
          <EditableTemplateCard
            key={i}
            t={t}
            onTitle={(v) => setTpl(i, 'title', v)}
            onText={(v) => setTpl(i, 'text', v)}
            onRemove={() => removeTpl(i)}
            onPickMedia={(e) => onPickMedia(i, e)}
            onRemoveMedia={(mi) => removeMedia(i, mi)}
            uploading={uploadingIdx === i}
            onPromote={admin ? () => promoteToAdmin(i) : undefined}
            promoting={promotingIdx === i}
          />
        ))}

        <button className='btn btn-sm btn-light-primary' onClick={addTpl}>
          <i className='bi bi-plus-lg me-1' /> הוסף הודעה
        </button>
      </div>
    </AffiliateShell>
  )
}

export default AffiliateWhatsappWrapper
